import { datadogRum } from '@datadog/browser-rum';
import { authExchange } from '@urql/exchange-auth';
import { createClient as createWSClient } from 'graphql-ws';
import { createClient, errorExchange, fetchExchange, subscriptionExchange } from 'urql';

import { TypedStorage } from '@/utils/TypedStorage';

import { env } from '../env';
import { AUTH_CONFIG } from './auth';
import { createAuthExchangeConfig } from './authExchange';
import { urqlCacheExchange } from './utils/cache-exchange';

import type { SubscribePayload } from 'graphql-ws';

function createSubscriptionClient() {
  const wsClient = createWSClient({
    url: env.NEXT_PUBLIC_GRAPHQL_WS_URL,
    // WS subscriptions bypass the fetch authExchange, so attach auth here,
    // evaluated per connection so the latest token is sent.
    //
    // share_token is intentionally omitted: it only grants read-only access to
    // insight reports for users without an Askable account, who don't use any
    // subscription features.
    connectionParams: () => {
      const token = AUTH_CONFIG.getToken();

      return token ? { Authorization: token } : {};
    },
    // Transport-level failures never surface as a urql operation result, so
    // without these handlers a broken subscription socket (failed handshake,
    // auth rejection, dropped connection) is completely invisible.
    on: {
      error: error => {
        datadogRum.addError(error, { source: 'graphql-ws', kind: 'connection_error' });
      },
      closed: event => {
        const code = typeof event === 'object' && event !== null ? (event as CloseEvent).code : undefined;
        // 1000/1001 are clean closes (navigation, unmount). 4499 is graphql-ws's
        // synthetic "Terminated" code for a frozen socket it auto-reconnects from,
        // so it's recoverable noise, not a failure. Anything else is unexpected.
        if (code !== undefined && code !== 1000 && code !== 1001 && code !== 4499) {
          datadogRum.addError(new Error(`GraphQL WS closed unexpectedly (${code})`), {
            source: 'graphql-ws',
            kind: 'connection_closed',
            code,
          });
        }
      },
    },
  });

  return subscriptionExchange({
    forwardSubscription(operation) {
      return {
        subscribe: sink => {
          const dispose = wsClient.subscribe(operation as SubscribePayload, sink);
          return {
            unsubscribe: dispose,
          };
        },
      };
    },
  });
}

export const client = createClient({
  url: `${env.NEXT_PUBLIC_GRAPHQL_URL}/graphql`,
  exchanges: [
    urqlCacheExchange,
    authExchange(async utils =>
      createAuthExchangeConfig(utils, {
        getToken: () => AUTH_CONFIG.getToken(),
        // Defer the lookup: AUTH_CONFIG.refreshToken is wired up by AuthProvider
        // *after* this client is created, so we can't snapshot it at module-eval.
        refreshToken: () => AUTH_CONFIG.refreshToken?.() ?? Promise.resolve(undefined),
        getShareToken: () => TypedStorage.get('share_token'),
        clientName: 'sessions',
        clientVersion: process.env.npm_package_version ?? 'N/A',
      }),
    ),
    // Subscriptions bypass authExchange and most components never read the
    // useSubscription `error` field, so GraphQL-level subscription errors would
    // otherwise vanish silently. Connection-level failures are already covered
    // by the graphql-ws `on` handlers, so only log graphQLErrors here to avoid
    // double-reporting the same dropped socket.
    errorExchange({
      onError: (error, operation) => {
        if (operation.kind === 'subscription' && error.graphQLErrors.length > 0) {
          datadogRum.addError(error, { source: 'graphql-subscription' });
        }
      },
    }),
    fetchExchange,
    createSubscriptionClient(),
  ],
});
